/**
 * The little glyph in front of a Document's name.
 *
 * <p>Hand-drawn SVG rather than an icon package, matching the nav icons in
 * `App.tsx`: ten glyphs is not a dependency, and these inherit `currentColor`
 * so they follow the theme without a second set for dark mode.
 *
 * <p>The classification reads the <b>extension first</b> and the MIME type
 * second, which is the opposite of what feels right. The reason is that browsers
 * are unreliable about the type on exactly the files this feature exists for: a
 * .docx arrives as `application/octet-stream` from several file managers, and
 * anything shared from a phone's share sheet frequently does too. The extension
 * is what the person typed and what their operating system agreed with.
 */

export type FileKind =
  | "pdf"
  | "word"
  | "excel"
  | "powerpoint"
  | "image"
  | "video"
  | "audio"
  | "archive"
  | "text"
  | "file";

/** Extension → kind, for the types worth telling apart at a glance. */
const BY_EXTENSION: Record<string, FileKind> = {
  pdf: "pdf",

  doc: "word",
  docx: "word",
  // Not Word, but they open in it and belong to the same shelf in anyone's head.
  odt: "word",
  rtf: "word",

  xls: "excel",
  xlsx: "excel",
  xlsm: "excel",
  ods: "excel",
  csv: "excel",

  ppt: "powerpoint",
  pptx: "powerpoint",
  odp: "powerpoint",

  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  webp: "image",
  bmp: "image",
  heic: "image",
  svg: "image",
  tif: "image",
  tiff: "image",

  mp4: "video",
  mov: "video",
  avi: "video",
  mkv: "video",
  webm: "video",

  mp3: "audio",
  wav: "audio",
  m4a: "audio",
  ogg: "audio",
  flac: "audio",

  zip: "archive",
  rar: "archive",
  "7z": "archive",
  gz: "archive",
  tar: "archive",

  txt: "text",
  md: "text",
  log: "text",
  json: "text",
  xml: "text",
  gpx: "text",
  kml: "text",
};

/** MIME prefix or exact type → kind, for when the extension says nothing. */
function kindFromType(contentType: string): FileKind | null {
  const type = contentType.toLowerCase().split(";")[0].trim();
  if (type === "application/pdf") return "pdf";
  if (type.startsWith("image/")) return "image";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";
  if (type.startsWith("text/")) return "text";
  // The OOXML and OpenDocument types are long and the distinguishing word is in
  // the middle of them, so this matches rather than compares.
  if (type.includes("wordprocessing") || type.includes("msword") || type.includes("opendocument.text"))
    return "word";
  if (type.includes("spreadsheet") || type.includes("ms-excel")) return "excel";
  if (type.includes("presentation") || type.includes("ms-powerpoint")) return "powerpoint";
  if (type.includes("zip") || type.includes("compressed") || type.includes("tar")) return "archive";
  if (type === "application/json" || type === "application/xml") return "text";
  return null;
}

export function fileKind(filename: string, contentType: string): FileKind {
  const dot = filename.lastIndexOf(".");
  const ext = dot > 0 ? filename.slice(dot + 1).toLowerCase() : "";
  return BY_EXTENSION[ext] ?? kindFromType(contentType ?? "") ?? "file";
}

/** What the kind is called out loud, for the icon's accessible name. */
const LABELS: Record<FileKind, string> = {
  pdf: "PDF",
  word: "Word document",
  excel: "Spreadsheet",
  powerpoint: "Presentation",
  image: "Image",
  video: "Video",
  audio: "Audio",
  archive: "Archive",
  text: "Text file",
  file: "File",
};

export function FileTypeIcon({
  filename,
  contentType,
  className,
}: {
  filename: string;
  contentType: string;
  className?: string;
}) {
  const kind = fileKind(filename, contentType);
  return (
    <svg
      className={`doc-icon doc-icon-${kind}${className ? ` ${className}` : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={LABELS[kind]}
    >
      {glyph(kind)}
    </svg>
  );
}

/**
 * The marks inside the icon. Every kind but `image` and `archive` is the same
 * folded page with a different device on it, so the set reads as one family and
 * the differences land where the eye is already looking.
 */
function glyph(kind: FileKind) {
  const page = (
    <>
      <path d="M6 2.75h7L18.25 8v13.25H6z" />
      <path d="M13 2.75V8h5.25" />
    </>
  );
  switch (kind) {
    case "pdf":
      return (
        <>
          {page}
          {/* The letters, not an abstract mark: PDF is the one type everyone
              recognises by name, and three letters at this size are legible
              where a glyph would be a smudge. */}
          <text
            x="12"
            y="17.5"
            textAnchor="middle"
            fontSize="5.5"
            fontWeight="700"
            fill="currentColor"
            stroke="none"
          >
            PDF
          </text>
        </>
      );
    case "word":
      return (
        <>
          {page}
          {/* Ruled lines — a page of prose. */}
          <path d="M8.5 12.5h7M8.5 15.5h7M8.5 18.5h4" />
        </>
      );
    case "excel":
      return (
        <>
          {page}
          {/* A grid. Three columns rather than two, so it cannot be mistaken
              for the ruled lines above. */}
          <path d="M8.5 12h7v7h-7z" />
          <path d="M8.5 15.5h7M10.83 12v7M13.17 12v7" />
        </>
      );
    case "powerpoint":
      return (
        <>
          {page}
          {/* A slide on a stand. */}
          <rect x="8.5" y="12" width="7" height="5" rx="0.6" />
          <path d="M12 17v2.5M10.25 19.5h3.5" />
        </>
      );
    case "image":
      return (
        <>
          <rect x="2.75" y="4.75" width="18.5" height="14.5" rx="2" />
          <circle cx="8.5" cy="10" r="1.6" />
          <path d="M3.5 17.5l4.75-4.75 3.5 3.5 3-2.75 5.75 5" />
        </>
      );
    case "video":
      return (
        <>
          {page}
          <path d="M10.5 12.75l4.75 3-4.75 3z" />
        </>
      );
    case "audio":
      return (
        <>
          {page}
          <path d="M10 18.5v-5.25l4.5-1v5.25" />
          <circle cx="9" cy="18.5" r="1.5" />
          <circle cx="13.5" cy="17.5" r="1.5" />
        </>
      );
    case "archive":
      return (
        <>
          <path d="M3.5 7.25h17v13a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z" />
          <path d="M2.75 3.75h18.5v3.5H2.75z" />
          {/* The zip's pull-tab, which is what makes it read as a zip rather
              than as a box. */}
          <path d="M11 11h2v2h-2zM11 14.5h2v2h-2z" />
        </>
      );
    case "text":
      return (
        <>
          {page}
          <path d="M8.5 12.5h7M8.5 15.5h7M8.5 18.5h2.5" />
          <path d="M8.5 9.5h2.5" />
        </>
      );
    default:
      return page;
  }
}
